import React, { Component } from "react";
import { FormLabel } from "@mui/material";
import {
  Alignment,
  Bold,
  ClassicEditor,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  SourceEditing,
  Table,
  TableToolbar,
  Underline,
  Undo,
} from "ckeditor5";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";
import BlockTypography from "../../ckeditor/blockTypographyPlugin";

const HTML_SUPPORT_ALLOW = [
  { name: "table", attributes: true, classes: true, styles: true },
  { name: "thead", attributes: true, classes: true, styles: true },
  { name: "tbody", attributes: true, classes: true, styles: true },
  { name: "tr", attributes: true, classes: true, styles: true },
  { name: "td", attributes: true, classes: true, styles: true },
  { name: "th", attributes: true, classes: true, styles: true },
  { name: "span", attributes: true, classes: true, styles: true },
  { name: "p", attributes: true, classes: true, styles: true },
  { name: "div", attributes: true, classes: true, styles: true },
  { name: "h1", attributes: true, classes: true, styles: true },
  { name: "h2", attributes: true, classes: true, styles: true },
  { name: "h3", attributes: true, classes: true, styles: true },
  { name: "h4", attributes: true, classes: true, styles: true },
  { name: "h5", attributes: true, classes: true, styles: true },
  { name: "h6", attributes: true, classes: true, styles: true },
  { name: "strong", attributes: true, classes: true, styles: true },
  { name: "em", attributes: true, classes: true, styles: true },
  { name: "a", attributes: true, classes: true, styles: true },
  { name: "img", attributes: true, classes: true, styles: true },
  { name: "ul", attributes: true, classes: true, styles: true },
  { name: "ol", attributes: true, classes: true, styles: true },
  { name: "li", attributes: true, classes: true, styles: true },
  { name: "blockquote", attributes: true, classes: true, styles: true },
  { name: "hr", attributes: true, classes: true, styles: true },
  { name: "br", attributes: true, classes: true, styles: true },
];

const HTML_SUPPORT_SVG_AND_ICONS = [
  { name: "i", attributes: true, classes: true, styles: true },
  { name: "b", attributes: true, classes: true, styles: true },
  { name: "u", attributes: true, classes: true, styles: true },
  { name: "sup", attributes: true, classes: true, styles: true },
  { name: "sub", attributes: true, classes: true, styles: true },
  { name: "svg", attributes: true, classes: true, styles: true },
  { name: "path", attributes: true, classes: true, styles: true },
  { name: "rect", attributes: true, classes: true, styles: true },
  { name: "circle", attributes: true, classes: true, styles: true },
  { name: "g", attributes: true, classes: true, styles: true },
  { name: "line", attributes: true, classes: true, styles: true },
  { name: "polyline", attributes: true, classes: true, styles: true },
  { name: "polygon", attributes: true, classes: true, styles: true },
];

class FormEditors extends Component {
  constructor(props) {
    super(props);
    this.editor = null;
    this.isSettingData = false;
  }

  componentDidUpdate(prevProps) {
    if (!this.editor || this.props.value === prevProps.value) {
      return;
    }

    const current = this.editor.getData();
    if (this.props.value !== current) {
      this.isSettingData = true;
      this.editor.setData(this.props.value || "");
      this.isSettingData = false;
    }
  }

  /**
   * Commit source-mode edits (if any) and return the latest HTML.
   * CKEditor only syncs the source textarea back to the model when source mode is closed.
   */
  flushAndGetData() {
    if (!this.editor) {
      return this.props.value || "";
    }

    const sourceEditing = this.editor.plugins.get("SourceEditing");
    if (sourceEditing?.isSourceEditingMode) {
      this.editor.execute("sourceEditing");
    }

    return this.editor.getData();
  }

  render() {
    const htmlSupportAllow = this.props.extendedHtmlSupport
      ? [...HTML_SUPPORT_ALLOW, ...HTML_SUPPORT_SVG_AND_ICONS]
      : HTML_SUPPORT_ALLOW;
    const enableBlockTypography = Boolean(this.props.enableBlockTypography);
    const toolbarItems = [
      "heading",
      "|",
      "fontFamily",
      "bold",
      "italic",
      "underline",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      ...(enableBlockTypography
        ? ["lineHeight", "paragraphSpacingBefore", "paragraphSpacing"]
        : []),
      "bulletedList",
      "numberedList",
      "alignment",
      "link",
      "insertTable",
      "horizontalLine",
      "undo",
      "redo",
      "|",
      "sourceEditing",
    ];
    const plugins = [
      Bold,
      Essentials,
      Italic,
      Mention,
      Paragraph,
      Undo,
      FontFamily,
      FontSize,
      FontColor,
      FontBackgroundColor,
      Underline,
      List,
      Alignment,
      HorizontalLine,
      Link,
      Heading,
      GeneralHtmlSupport,
      SourceEditing,
      Table,
      TableToolbar,
      ...(enableBlockTypography ? [BlockTypography] : []),
    ];

    return (
      <React.Fragment>
        <FormLabel style={{ fontFamily: "Manrope-SemiBold", fontSize: 14 }}>
          {this.props?.title}
        </FormLabel>
        <CKEditor
          editor={ClassicEditor}
          data={this.props?.value || ""}
          onReady={(editor) => {
            this.editor = editor;
            const previewClass = (this.props.contentPreviewClass || "")
              .split(/\s+/)
              .filter(Boolean);
            if (previewClass.length) {
              editor.ui.view.editable.element.classList.add(...previewClass);
            }
          }}
          onChange={(event, editor) => {
            if (this.isSettingData) {
              return;
            }
            this.props?.setValue(editor.getData());
          }}
          config={{
            toolbar: {
              items: toolbarItems,
            },
            heading: {
              options: [
                { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
                { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
                { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
                { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
              ],
            },
            htmlSupport: {
              allow: htmlSupportAllow,
            },
            table: {
              contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
            },
            placeholder: this.props?.placeholder || "",
            plugins,
          }}
        />
      </React.Fragment>
    );
  }
}

export default FormEditors;
